import React from 'react';
import moment from 'moment';
import { CellWithMoreActions } from 'components/renderers';
import { getSystemMessageEndDateFormat, getSystemMessageStartDateFormat } from 'utils/datetime';
import { getBase64FromFile, getFileFromBase64 } from 'utils/file';
import { SYSTEM_MESSAGE_PLATFORMS } from 'constants/constants';
import SM from 'services/ServiceManager';
import { Loader } from 'ui-library';

export const getSystemMessageColumns = ({ t }) => [
    {
        key: 'created_date',
        title: `${t('SystemMessage.Table.Header.Created')}`,
    },
    {
        key: 'created_by',
        title: t('SystemMessage.Table.Header.CreatedBy'),
    },
    {
        key: 'title',
        title: t('SystemMessage.Table.Header.Title'),
    },
    {
        key: 'start_date',
        title: t('SystemMessage.Table.Header.StartDate'),
    },
    {
        key: 'end_date',
        title: t('SystemMessage.Table.Header.EndDate'),
    },
    {
        key: 'platforms',
        title: t('SystemMessage.Table.Header.Platforms'),
    },
    {
        key: 'actions',
        title: '',
        titleType: 'columns',
        width: 40,
        minWidth: 40,
        fixed: 'right',
        render: CellWithMoreActions,
    },
];

export type SystemMessageStatus = 'active' | 'inActive' | 'expired';

export function getStatus(startDate: string | Date, endDate: string | Date): SystemMessageStatus {
    const now = new Date().getTime();
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();

    if (typeof endDate === 'string' && !endDate.includes('T23:59:00Z') && start <= now) {
        return 'expired'; // if endDate is in string format and does not include time, consider it as expired
    }

    if (now > end) return 'expired'; // expired
    if (now < start) return 'inActive'; // not started yet

    return 'active'; // between start and end
}

export const adaptSystemMessageData = ({
    data,
    handleOpenMessage,
    loadingEditMessage,
    t,
}) => {
    const PLATFORM_OPTIONS = getPlatformOptions(t);

    return data?.map((message) => {
        const messageStatus = getStatus(
            message?.startDateTime,
            message?.endDateTime,
        );

        return {
            title: message?.title,
            created_by: message?.createdByDisplayName,
            created_date: (
                <span
                    className={`system-message-created-date message-${messageStatus}`}
                >
                    {moment(message?.createdDateTime).format('DD.MM.YY, HH:mm')}
                </span>
            ),
            start_date: message?.startDateTime?.includes('T00:01:00Z')
                ? moment.utc(message?.startDateTime).format('DD.MM.YY, HH:mm')
                : moment(message?.startDateTime).format('DD.MM.YY, HH:mm'),
            end_date: message?.endDateTime?.includes('T23:59:00Z')
                ? moment.utc(message?.endDateTime).format('DD.MM.YY, HH:mm')
                : moment(message?.endDateTime).format('DD.MM.YY, HH:mm'),
            platforms: (
                <span style={{ whiteSpace: 'pre-line' }}>
                    {PLATFORM_OPTIONS?.find(
                        (platform) => +platform.value === message?.platform,
                    )?.label?.replace(/&/g, '\n')}
                </span>
            ),
            actions: {
                value: 'action',
                actions: [
                    {
                        text: (
                            <div className="system-message-option-items">
                                <span>
                                    {t(
                                        'SystemMessage.Table.ActionBtn.ShowPreview',
                                    )}
                                    {' '}
                                </span>
                                {loadingEditMessage === 'preview-message' ? (
                                    <Loader small />
                                ) : null}
                            </div>
                        ),
                        onClick: (e) => handleOpenMessage({
                            e,
                            data: message,
                            type: 'preview-message',
                        }),
                        hidden: messageStatus === 'expired',
                    },
                    {
                        text: (
                            <span className="system-message-option-items">
                                {t('SystemMessage.Table.ActionBtn.EditMessage')}
                                {' '}
                                {loadingEditMessage === 'edit-message' ? (
                                    <Loader small />
                                ) : null}
                            </span>
                        ),
                        onClick: (e) => handleOpenMessage({
                            e,
                            data: message,
                            type: 'edit-message',
                        }),
                        hidden: messageStatus === 'expired',
                    },
                    {
                        text: (
                            <span className="system-message-option-items">
                                {t(
                                    'SystemMessage.Table.ActionBtn.DuplicateMessage',
                                )}
                                {' '}
                                {loadingEditMessage === 'duplicate-message' ? (
                                    <Loader small />
                                ) : null}
                            </span>
                        ),
                        onClick: (e) => handleOpenMessage({
                            e,
                            data: message,
                            type: 'duplicate-message',
                        }),
                    },
                    {
                        text: (
                            <span className="system-message-option-items">
                                {t(
                                    messageStatus === 'active'
                                        ? 'SystemMessage.Table.ActionBtn.DeactivateMessage'
                                        : 'clientDashboard.delete',
                                )}
                                {' '}
                            </span>
                        ),
                        onClick: (e) => handleOpenMessage({
                            e,
                            data: message,
                            type: 'delete-message',
                        }),
                        hidden: messageStatus === 'expired',
                    },
                ],
            },
        };
    });
};

export const adaptCreateSystemMessagePayload = ({ data, oldMessageData = {} } : { data: any, oldMessageData?: any }) => {
    const languages = Object.keys(data.translations || {});

    const title = {};
    const body = {};
    const url = {};

    languages.forEach((lang) => {
        const t = data.translations[lang] || {};

        title[lang] = t.title || '';
        body[lang] = t.body || '';
        url[lang] = t.link || '';
    });

    return {
        platform: +(data?.platform || 0),
        startDateTime: oldMessageData?.startDateDisabled ? moment
            .utc(oldMessageData.startDate)
            .format('YYYY-MM-DDTHH:mm:ss[Z]') : getSystemMessageStartDateFormat({ date: data.startDate }),
        endDateTime: getSystemMessageEndDateFormat({ date: data.endDate }),
        title,
        body,
        url,
    };
};

export const adaptFile = async (file: File) => {
    const base64File = await getBase64FromFile(file as File);
    const splitted = base64File.split(',');

    const adapted = {
        fileName: file.name,
        fileType: file.type,
        file: splitted[1],
    };

    return adapted;
};

export const getPlatformOptions = (t) => [
    {
        label: t('SystemMessage.Form.PlatformOptions.Label.mobileAdvisor'),
        value: SYSTEM_MESSAGE_PLATFORMS.MobileAdvisor,
    },
    {
        label: t('SystemMessage.Form.PlatformOptions.Label.clientCockpit'),
        value: SYSTEM_MESSAGE_PLATFORMS.ClientCockpit,
    },
    {
        label: t('SystemMessage.Form.PlatformOptions.Label.both'),
        value: SYSTEM_MESSAGE_PLATFORMS.All,
    },
];

export const adaptSystemMessageDetailsData = ({
    data,
    documents,
    duplicate,
    languages,
}: {
    data: any;
    documents: any;
    duplicate: boolean;
    languages: string[];
}) => {
    const translations = languages.reduce((acc, lang) => {
        acc[lang] = {
            title: data?.title?.[lang],
            body: data?.body?.[lang],
            link: data?.url?.[lang],
            document: documents?.[lang]?.file,
            documentId: documents?.[lang]?.id,
        };

        return acc;
    }, {} as Record<string, any>);

    return {
        id: data?.id,
        uId: data?.uid,
        startDateDisabled: duplicate ? false : moment.utc(data?.startDateTime).isSame(moment.utc(), 'day'),
        startDate: duplicate ? '' : moment.utc(data?.startDateTime),
        endDate: duplicate ? '' : moment.utc(data?.endDateTime),
        platform: `${data?.platform}`,
        translations,
    };
};

export const fetchDocumentData = async ({ documents, getRawData = false }) => {
    try {
        const fetchPromises = documents.map(async (doc) => {
            const response: any = await SM.documents('getDocumentById', [doc?.id]);

            if (response.status !== 200) {
                throw new Error('general.somethingWentWrong');
            }

            if (getRawData) {
                return {
                    [doc?.languageTwoLetterIsoCode]: response?.data,
                };
            }

            const data = await getFileFromBase64(
                response?.data?.file,
                response?.data?.filename,
                response?.data?.mimeType,
            );

            return {
                [doc?.languageTwoLetterIsoCode]: {
                    file: data,
                    id: doc?.id,
                },
            };
        });

        const resultsArray = await Promise.allSettled(fetchPromises);

        const filteredArray = resultsArray
            ?.filter((result) => {
                if (result.status === 'rejected') {
                    // eslint-disable-next-line no-console
                    console.error('Document fetch failed:', result.reason);
                }

                return result.status === 'fulfilled';
            })
            ?.map((result) => result?.value);

        return Object.assign({}, ...filteredArray);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error updating document array:', error);
        throw error;
    }
};

export const adaptPreviewMessageData = ({ data, documents, t }) => {
    const PLATFORM_OPTIONS = getPlatformOptions(t);

    return {
        translations: {
            title: data?.title,
            body: data?.body,
            link: data?.url,
            document: documents,
            documentId: documents?.id,
        },
        startDate: data?.startDateTime,
        endDate: data?.endDateTime,
        platforms: PLATFORM_OPTIONS?.find(
            (platform) => +platform.value === data?.platform,
        )?.label,
    };
};

export const getDirtyValues = (dirtyFields, allValues) => {
    if (dirtyFields === true) return allValues;

    return Object.keys(dirtyFields).reduce((acc, key) => {
        acc[key] = getDirtyValues(dirtyFields[key], allValues[key]);

        return acc;
    }, {});
};
